import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Input, Label } from 'reactstrap';
import Select from 'react-select';


// Helper to format date strings into 'YYYY-MM-DD' for the date input
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        // This handles full ISO strings and simple date strings
        return new Date(dateString).toISOString().split('T')[0];
    } catch (e) {
        return ''; // Return empty if date is invalid
    }
};

const AddNewLawForm = ({ labels, formikProps, baseLaws = [], isAddOrEdit }) => {
    const { t } = useTranslation();

    // Destructure from formikProps for easier access
    const { values, errors, touched, setFieldValue, handleBlur, handleChange } = formikProps;

    // This useEffect hook handles the logic for auto-populating fields.
    // When a user selects a "Law Name" from the dropdown, it finds that law
    // in the `baseLaws` array and fills in the 'Law No.', 'Enacted On', and 'Total Articles' fields.
    useEffect(() => {
        if (values.LawID) {
            const selectedLaw = baseLaws.find(law => law.id === values.LawID);
            
            if (selectedLaw) {
                // Use setFieldValue to update Formik's state for dependent fields
                setFieldValue('LawNo', selectedLaw.LawNo || '');
                setFieldValue('EnactedOn', formatDateForInput(selectedLaw.EnactedOn));
                setFieldValue('TotalArticles', selectedLaw.TotalArticles || '');
            }
        } else {
             // If LawID is cleared in 'Add' mode, also clear the dependent fields
            if (!isAddOrEdit) {
                setFieldValue('LawNo', '');
                setFieldValue('EnactedOn', '');
                setFieldValue('TotalArticles', '');
            }
        }
    }, [values.LawID, baseLaws, setFieldValue, isAddOrEdit]);


    /**
     * Renders a specific form field based on the configuration object.
     * @param {object} item - The field configuration from the `labels` array.
     */
    const renderField = (item) => {
        // In 'edit' mode, we disable the base law information fields.
        // In 'add' mode, we respect the 'disabled' property from the config.
        const isDisabledInEditMode = isAddOrEdit && ['LawID', 'LawNo', 'EnactedOn', 'TotalArticles'].includes(item.name);
        const isDisabled = isDisabledInEditMode || (item.disabled && !isAddOrEdit);
            
        const fieldValue = values[item.name] ?? "";

        switch (item.fieldName) {
            case 'select-box':
                // Creates options for the react-select dropdown from the baseLaws prop
                const options = baseLaws.map(law => ({
                    value: law.id,
                    label: `${law.name} (${law.LawNo})` // A more user-friendly label
                }));

                const selectedOption = options.find(option => option.value === fieldValue);
                
                return (
                    <Select
                        id={item.name}
                        name={item.name}
                        placeholder={t('Select a Law...')}
                        options={options}
                        value={selectedOption || null}
                        onChange={(option) => setFieldValue(item.name, option ? option.value : '')}
                        onBlur={handleBlur}
                        className={touched[item.name] && errors[item.name] ? 'is-invalid' : ''}
                        isDisabled={isDisabled}
                    />
                );

            case 'date-picker':
                return (
                    <Input
                        type="date"
                        id={item.name}
                        name={item.name}
                        value={formatDateForInput(fieldValue)} // Use formatted date
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[item.name] && !!errors[item.name]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'number-box':
                return (
                    <Input
                        type="text" // Using text allows for custom filtering of input
                        id={item.name}
                        name={item.name}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={(e) => {
                            // Allow only numeric characters
                            const numericValue = e.target.value.replace(/[^0-9]/g, '');
                            setFieldValue(item.name, numericValue);
                        }}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[item.name] && !!errors[item.name]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );
            
            case 'text-box':
                return (
                    <Input
                        type="text"
                        id={item.name}
                        name={item.name}
                        placeholder={t(item.placeholder || "e.g., 1, 5, 10-15")}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[item.name] && !!errors[item.name]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <Row>
            {labels.map((item) => (
                <Col sm={6} key={item.id}>
                    <div className="mb-3">
                        <Label htmlFor={item.name} className="form-label">
                            {t(item.labelName)}
                            {item.required && <span className="text-danger"> *</span>}
                        </Label>
                        
                        {/* Render the appropriate field */}
                        {renderField(item)}

                        {/* Display validation errors */}
                        {touched[item.name] && errors[item.name] && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                                {t(errors[item.name])}
                            </div>
                        )}
                    </div>
                </Col>
            ))}
        </Row>
    );
};

export default AddNewLawForm;